// Amplify v6 headless auth (D-20). The only Cognito library in this SPA.
// Credential forms are served from this origin; nothing ever redirects to
// *.amazoncognito.com (D-21). There is no federation in v1, so configure()
// does not include an oauth domain.

import { Amplify } from "aws-amplify";
import {
  confirmResetPassword,
  confirmSignIn,
  confirmUserAttribute,
  fetchAuthSession,
  fetchMFAPreference,
  resetPassword,
  setUpTOTP,
  signIn,
  signOut,
  updateMFAPreference,
  updatePassword,
  updateUserAttribute,
  verifyTOTPSetup,
} from "aws-amplify/auth";

const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const userPoolClientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

export function isAuthConfigured(): boolean {
  return Boolean(userPoolId && userPoolClientId);
}

if (isAuthConfigured()) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: userPoolId!,
        userPoolClientId: userPoolClientId!,
      },
    },
  });
}

export {
  confirmResetPassword,
  confirmSignIn,
  confirmUserAttribute,
  fetchAuthSession,
  fetchMFAPreference,
  resetPassword,
  setUpTOTP,
  signIn,
  signOut,
  updateMFAPreference,
  updatePassword,
  updateUserAttribute,
  verifyTOTPSetup,
};

/**
 * ID token, not the access token. The HTTP API JwtAuthorizer is configured
 * with jwtAudience = the SPA client id, which Cognito puts on `aud` of the
 * ID token. Access tokens carry `client_id` instead and are rejected as 401.
 * Session 2's smoke script hits the same constraint.
 */
export async function getIdToken(): Promise<string | null> {
  if (!isAuthConfigured()) return null;
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? null;
}

export async function isSignedIn(): Promise<boolean> {
  return Boolean(await getIdToken());
}

export function emailFromIdToken(token: string): string | undefined {
  try {
    const payload = JSON.parse(atob(token.split(".")[1] ?? "")) as { email?: unknown };
    return typeof payload.email === "string" ? payload.email : undefined;
  } catch {
    return undefined;
  }
}
